import { Checkbox } from 'Components/Core';
import { GridFieldSection, Grid } from 'Components/Layout';
import { actions } from 'Data/ReduxViewModels/Admin';
import { useDispatch } from 'react-redux';
import {
  FieldName,
  FieldVisibilityOption,
  IOptionalWorkOrderFieldsModel,
  IWorkOrderFieldVisibilityGrid,
  ProductName,
} from '../../types';
import './WorkOrderFieldVisibilityGrid.scss';

const WorkOrderFieldVisibilityGrid: React.FC<IWorkOrderFieldVisibilityGrid> = ({ Label, Product, Data, Disabled }) => {
  // Key types
  type DataKey = keyof typeof Data;
  type FieldsKey = keyof IOptionalWorkOrderFieldsModel;

  const dispatch = useDispatch();
  let localData = { ...Data[ProductName[Product] as DataKey] };

  // Returns value of the Checkbox (checked, unchecked)
  const getCheckboxValue = (field: FieldName, editableProperty: Boolean) => {
    const option = localData[FieldName[field] as FieldsKey] ?? FieldVisibilityOption.NotVisible;

    switch (option) {
      case FieldVisibilityOption.VisibleEditable:
        return true;
      case FieldVisibilityOption.Visible:
        return !editableProperty;
    }

    return false;
  };

  // Action on changing Checkbox value
  const onCheckboxChange = (evtData: any, editableProperty: Boolean) => {
    let localDataTmp = { ...localData };

    // switch by current Option
    switch (localData[evtData.name as FieldsKey]) {
      case FieldVisibilityOption.NotVisible:
        if (!editableProperty && evtData.value) {
          // Visible checkbox clicked
          localDataTmp[evtData.name as FieldsKey] = FieldVisibilityOption.Visible;
        }

        break;

      case FieldVisibilityOption.Visible:
        if (!editableProperty && !evtData.value) {
          // Visible checkbox clicked
          localDataTmp[evtData.name as FieldsKey] = FieldVisibilityOption.NotVisible;
        } else if (editableProperty && evtData.value) {
          // Editable checkbox clicked
          localDataTmp[evtData.name as FieldsKey] = FieldVisibilityOption.VisibleEditable;
        }

        break;

      case FieldVisibilityOption.VisibleEditable:
        if (!editableProperty && !evtData.value) {
          // Visible checkbox clicked
          localDataTmp[evtData.name as FieldsKey] = FieldVisibilityOption.NotVisible;
        } else if (editableProperty && !evtData.value) {
          // Editable checkbox clicked
          localDataTmp[evtData.name as FieldsKey] = FieldVisibilityOption.Visible;
        }

        break;
    }

    localData = localDataTmp;

    dispatch(actions.setValue({ name: 'syncModel.' + ProductName[Product], value: localDataTmp, sync: true }));
  };

  // Creates a single Checkbox
  const createdSingleCheckbox = (field: FieldName, editableProperty: Boolean) => (
    <Checkbox
      name={FieldName[field]}
      value={getCheckboxValue(field, editableProperty)}
      onChange={(evt: any) => onCheckboxChange(evt.data, editableProperty)}
      disabled={Disabled || (editableProperty && !getCheckboxValue(field, false))}
    />
  );

  // Generates row with checkboxes for a single field
  const createdFieldCheckboxes = (field: FieldName) => [
    createdSingleCheckbox(field, false),
    createdSingleCheckbox(field, true),
  ];

  // An Array with all checkboxes for given product
  const data = [
    createdFieldCheckboxes(FieldName.OrderNo),
    createdFieldCheckboxes(FieldName.ClientRequestId),
    createdFieldCheckboxes(FieldName.TRN),
    createdFieldCheckboxes(FieldName.LotNo),
  ];

  return (
    <GridFieldSection heading={Label} additionalClassName="WorkOrderFieldVisibilityGrid">
      <Grid
        cornerHeader="Field"
        rowHeaders={['Order No.', 'Client Request ID', 'TRN', 'Lot No.']}
        rowHeaderWidth={150}
        colWidths={100}
        minColWidths={100}
        colHeaders={['Visible', 'Editable']}
        data={data}
        colCellAlignments={'center'}
      />
    </GridFieldSection>
  );
};

export { WorkOrderFieldVisibilityGrid };
